import { Button } from '@solinkcorp/solink-ui/components/Button';
import { buttonVariants } from '@solinkcorp/solink-ui/components/ui/button';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

const YEAR_PAGE = 12;

const navButtonClassName = cn(
  buttonVariants({ variant: 'ghost' }),
  'size-8 select-none rounded-lg border border-border-muted p-1',
);

const captionToggleClassName = cn(
  'h-8 rounded-lg px-2 text-text-primary label-md',
  'hover:bg-interaction-secondary-background-default',
);

// Same hover and selected fill as a day cell. `aria-pressed` marks the current month or
// year, and its hover rule outranks the plain hover so the accent fill stays put.
const periodOptionClassName = cn(
  'h-9 w-full rounded-md px-1 text-text-primary label-sm',
  'transition-none',
  'hover:bg-interaction-secondary-background-default hover:text-text-primary',
  'dark:hover:bg-interaction-secondary-background-default dark:hover:text-text-primary',
  'disabled:text-interaction-secondary-content-inactive',
  'aria-pressed:bg-background-accent aria-pressed:text-text-primary',
  'aria-pressed:hover:bg-background-accent',
  'dark:aria-pressed:bg-background-accent dark:aria-pressed:hover:bg-background-accent',
  'dark:aria-pressed:hover:text-text-primary',
);

export type PickerView = 'day' | 'month' | 'year';

function yearPageContaining(year: number, minYear: number) {
  const offset = Math.max(0, year - minYear);
  return minYear + Math.floor(offset / YEAR_PAGE) * YEAR_PAGE;
}

/** Month grid, then a year grid. Clicking the header moves one level up, matching mobile. */
export function PeriodJump({
  viewMode,
  browseYear,
  minYear,
  maxYear,
  minDay,
  maxDay,
  selectedMonth,
  localeCode,
  onBrowseYear,
  onShowMonths,
  onShowYears,
  onPickMonth,
}: {
  viewMode: Exclude<PickerView, 'day'>;
  browseYear: number;
  minYear: number;
  maxYear: number;
  minDay: Date | undefined;
  maxDay: Date;
  selectedMonth: Date;
  localeCode: string;
  onBrowseYear: (year: number) => void;
  onShowMonths: () => void;
  onShowYears: () => void;
  onPickMonth: (monthIndex: number) => void;
}) {
  const [yearPageStart, setYearPageStart] = useState(() => yearPageContaining(browseYear, minYear));
  const isYearView = viewMode === 'year';
  const pageEnd = Math.min(maxYear, yearPageStart + YEAR_PAGE - 1);
  const canGoPrev = isYearView ? yearPageStart > minYear : browseYear > minYear;
  const canGoNext = isYearView ? yearPageStart + YEAR_PAGE <= maxYear : browseYear < maxYear;

  const months = Array.from({ length: 12 }, (_, index) => ({
    index,
    label: new Date(browseYear, index, 1).toLocaleString(localeCode, { month: 'short' }),
  }));
  const years = Array.from({ length: YEAR_PAGE }, (_, index) => yearPageStart + index);

  return (
    <div className="flex min-h-[21rem] flex-col gap-3">
      <div className="flex h-9 items-center justify-between">
        <button
          type="button"
          className={navButtonClassName}
          aria-label={isYearView ? 'Previous years' : 'Previous year'}
          disabled={!canGoPrev}
          onClick={() =>
            isYearView
              ? setYearPageStart(Math.max(minYear, yearPageStart - YEAR_PAGE))
              : onBrowseYear(browseYear - 1)
          }
        >
          <ChevronLeft className="size-4" aria-hidden />
        </button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={isYearView}
          className={captionToggleClassName}
          onClick={() => {
            if (isYearView) {
              onShowMonths();
              return;
            }
            setYearPageStart(yearPageContaining(browseYear, minYear));
            onShowYears();
          }}
        >
          {isYearView ? `${yearPageStart} \u2013 ${pageEnd}` : browseYear}
          <ChevronDown
            className={cn(
              'size-3.5 text-text-muted transition-transform',
              isYearView && 'rotate-180',
            )}
            aria-hidden
          />
        </Button>
        <button
          type="button"
          className={navButtonClassName}
          aria-label={isYearView ? 'Next years' : 'Next year'}
          disabled={!canGoNext}
          onClick={() =>
            isYearView ? setYearPageStart(yearPageStart + YEAR_PAGE) : onBrowseYear(browseYear + 1)
          }
        >
          <ChevronRight className="size-4" aria-hidden />
        </button>
      </div>
      <div className="grid flex-1 grid-cols-4 content-center gap-2">
        {isYearView
          ? years.map((year) => (
              <PeriodOption
                key={year}
                label={`${year}`}
                selected={year === selectedMonth.getFullYear()}
                disabled={year < minYear || year > maxYear}
                onClick={() => {
                  onBrowseYear(year);
                  onShowMonths();
                }}
              />
            ))
          : months.map((monthOption) => {
              const monthStart = new Date(browseYear, monthOption.index, 1);
              const monthEnd = new Date(browseYear, monthOption.index + 1, 0);
              return (
                <PeriodOption
                  key={monthOption.index}
                  label={monthOption.label}
                  selected={
                    browseYear === selectedMonth.getFullYear() &&
                    monthOption.index === selectedMonth.getMonth()
                  }
                  disabled={(minDay !== undefined && monthEnd < minDay) || monthStart > maxDay}
                  onClick={() => onPickMonth(monthOption.index)}
                />
              );
            })}
      </div>
    </div>
  );
}

function PeriodOption({
  label,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={disabled}
      aria-pressed={selected}
      onClick={onClick}
      className={periodOptionClassName}
    >
      {label}
    </Button>
  );
}
